/* eslint-disable @typescript-eslint/no-var-requires */
import { TabItem } from './navigation';
import { renderLanguagePicker } from './language-picker';
import { MAX_MOBILE_WINDOW_WIDTH, ScreenSize } from '../util/constants';
import { translate } from '../i18n';
import { always, getVisibilityFunctions } from '../util/context-visibility';
import KatalMetricsPublisher from '@amzn/katal-metrics/lib/KatalMetricsPublisher';
import '../styles/quick-links.scss';
import { HomepageContext } from '@amzn/brand-context-service-type-script-client';
import { formatMetricString, metricsPublisher } from '../util/metrics';

const settingsDropdownOptions: TabItem[] = [
  {
    id: 'email-preferences',
    link: '/preferences/customer',
    shownWhen: always,
    stringId: 'br-navigation-settings-email-preferences',
  },
  {
    id: 'login-settings',
    link: '/gp/seller/configuration/login-settings.html',
    shownWhen: always,
    stringId: 'br-navigation-settings-login-settings',
  },
  {
    id: 'user-permissions',
    link: '/brg/user-permissions',
    shownWhen: ({ customerIs }) => customerIs(['ADMINISTRATOR']),
    stringId: 'br-navigation-settings-user-permissions',

  },
  {
    id: 'log-out',
    link: '/gp/sign-in/logout.html',
    shownWhen: always,
    stringId: 'br-navigation-settings-log-out',
  },
];

let quickLinksPublisher: KatalMetricsPublisher;

const renderSettings = (context: HomepageContext) => {
  const visibilityFunctions = getVisibilityFunctions(context);

  const settingsContainer = document.createElement('div');
  const settingsContainerSelector = 'brand-registry-mobile-settings-container';
  settingsContainer.className = settingsContainerSelector;
  settingsContainer.id = settingsContainerSelector;

  const settingsIcon = document.createElement('img');
  settingsIcon.src = require('../images/settings.svg').default;

  const settingsLink = document.createElement('a');
  settingsLink.href = '/';

  settingsLink.className = 'brand-registry-nav-settings-link';
  settingsLink.id = 'brand-registry-nav-settings-link';

  settingsLink.appendChild(settingsIcon);

  const settingsInnerContainer = document.createElement('div');
  settingsInnerContainer.className = 'brand-registry-settings-tab';
  settingsInnerContainer.id = 'brand-registry-settings-tab';
  const tabPopoverContainer = document.createElement('div');
  const tabPopoverContainerDefaultSelector = 'brand-registry-settings-tab-popover-container';
  tabPopoverContainer.className = tabPopoverContainerDefaultSelector;
  tabPopoverContainer.id = tabPopoverContainerDefaultSelector;

  settingsLink.onclick = (e) => {
    e.preventDefault();

    if (tabPopoverContainer.className === tabPopoverContainerDefaultSelector) {
      tabPopoverContainer.className = `${tabPopoverContainerDefaultSelector}-open`;
      quickLinksPublisher.publishCounter('SettingsTabOpened', 1);
    } else {
      tabPopoverContainer.className = tabPopoverContainerDefaultSelector;
      quickLinksPublisher.publishCounter('SettingsTabClosed', 1);
    }
  };

  const list = document.createElement('ul');

  const filteredSettingsDropdownOptions = settingsDropdownOptions
    .filter((option) => option.shownWhen(visibilityFunctions));
  filteredSettingsDropdownOptions.forEach((option, i) => {
    const translatedOption = translate(option.stringId);

    const subItemMetricString = formatMetricString(option.id);

    const subItemLink = document.createElement('a');
    const subItem = document.createElement('li');
    subItemLink.textContent = translatedOption;
    subItemLink.href = option.link;
    subItemLink.onclick = () => {
      tabPopoverContainer.className = tabPopoverContainerDefaultSelector;
      quickLinksPublisher.publishCounter(`${subItemMetricString}LinkClicked`, 1);
      window.location.href = option.link;
    };
    // if logout option, give it a bottom border
    if (i === filteredSettingsDropdownOptions.length - 1) {
      subItem.className = 'brand-registry-logout-option';
    }
    subItemLink.id = `brand-registry-${option.id}-settings-tab-link`;
    subItemLink.ariaLabel = `brand-registry-${option.id}-settings-tab-link`;
    subItem.appendChild(subItemLink);
    list.appendChild(subItem);
  });

  tabPopoverContainer.appendChild(list);
  settingsInnerContainer.appendChild(settingsLink);
  settingsInnerContainer.appendChild(tabPopoverContainer);

  settingsContainer.appendChild(settingsInnerContainer);

  // if clicked out of, close the settings dropdown
  document.addEventListener('click', (e) => {
    if (
      !e.composedPath().includes(settingsContainer) &&
        tabPopoverContainer.className === `${tabPopoverContainerDefaultSelector}-open`
    ) {
      tabPopoverContainer.className = tabPopoverContainerDefaultSelector;
      quickLinksPublisher.publishCounter('SettingsTabClosedClickedOutside', 1);
    }
  });

  return settingsContainer;
};

const renderFullScreenHelp = () => {
  const helpLink = document.createElement('a');
  helpLink.href = '/gp/help/202130410';
  helpLink.className = 'brand-registry-nav-help-link';
  helpLink.id = 'brand-registry-nav-help-link';
  helpLink.onclick = () => {
    quickLinksPublisher.publishCounter('HelpLinkClicked', 1);
  };

  const helpSpan = document.createElement('span');
  helpSpan.className = 'brand-registry-nav-help';
  helpSpan.textContent = translate('br-navigation-help-link');
  helpLink.appendChild(helpSpan);

  const helpContainer = document.createElement('div');
  helpContainer.className = 'brand-registry-nav-help-container';
  helpContainer.id = 'brand-registry-nav-full-screen-help-container';
  helpContainer.appendChild(helpLink);

  return helpContainer;
};

const renderMobileHelp = () => {
  const mobileDiv = document.createElement('div');
  mobileDiv.className = 'brand-registry-mobile-nav-quick-links-container';
  mobileDiv.id = 'brand-registry-mobile-nav-quick-links-container';

  const mobileHelpIcon = document.createElement('img');
  mobileHelpIcon.src = require('../images/help.svg').default;

  const helpLink = document.createElement('a');
  helpLink.href = '/gp/help/202130410';
  helpLink.className = 'brand-registry-nav-help-link';
  helpLink.id = 'brand-registry-nav-help-link';
  helpLink.onclick = () => {
    quickLinksPublisher.publishCounter('MobileHelpLinkClicked', 1);
  };

  const helpContainer = document.createElement('div');
  helpContainer.className = 'brand-registry-mobile-help-container';
  helpContainer.id = 'brand-registry-mobile-help-container';
  helpContainer.ariaLabel = 'brand-registry-mobile-help-link';

  helpLink.appendChild(mobileHelpIcon);
  helpContainer.appendChild(helpLink);
  mobileDiv.appendChild(helpContainer);

  return mobileDiv;
};

export const renderQuickLinks = (homepageContext: HomepageContext): HTMLDivElement => {
  quickLinksPublisher = metricsPublisher.newChildActionPublisherForMethod('QuickLinks');

  let screenSize: ScreenSize = window.innerWidth <= MAX_MOBILE_WINDOW_WIDTH ? 'mobile' : 'normal';

  const helpComponent = screenSize === 'mobile' ? renderMobileHelp() : renderFullScreenHelp();
  const quickLinksContainer = document.createElement('div');
  quickLinksContainer.className = 'brand-registry-quick-links-container';

  window.addEventListener('resize', () => {
    const oldHelpComponent = quickLinksContainer.children[2];
    if (window.innerWidth <= MAX_MOBILE_WINDOW_WIDTH && screenSize === 'normal' && oldHelpComponent) {
      screenSize = 'mobile';
      quickLinksContainer.replaceChild(renderMobileHelp(), oldHelpComponent);
    } else if (window.innerWidth > MAX_MOBILE_WINDOW_WIDTH && screenSize === 'mobile' && oldHelpComponent) {
      screenSize = 'normal';
      quickLinksContainer.replaceChild(renderFullScreenHelp(), oldHelpComponent);
    }
  });

  const settings = renderSettings(homepageContext);
  const languagePicker = renderLanguagePicker();

  quickLinksContainer.append(settings, languagePicker, helpComponent);

  return quickLinksContainer;
};
